import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { mediaService, MediaFile } from "../services/firebase";
import "./GalleryDetail.css";

const GalleryDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [galleryImages, setGalleryImages] = useState<MediaFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayedCount, setDisplayedCount] = useState(12); // התחלה עם 12 תמונות
  const observerTarget = useRef<HTMLDivElement>(null);
  const imagesPerLoad = 9; // כמה תמונות לטעון בכל פעם
  const [selectedImage, setSelectedImage] = useState<MediaFile | null>(null);

  // מציאת אינדקס התמונה הנוכחית
  const currentImageIndex = useMemo(() => {
    if (!selectedImage) return -1;
    return galleryImages.findIndex((img) => img.id === selectedImage.id);
  }, [selectedImage, galleryImages]);

  const closeModal = () => {
    setSelectedImage(null);
  };

  const goToPrevious = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentImageIndex > 0) {
      setSelectedImage(galleryImages[currentImageIndex - 1]);
    }
  };

  const goToNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentImageIndex < galleryImages.length - 1) {
      setSelectedImage(galleryImages[currentImageIndex + 1]);
    }
  };

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  // סגירת פופ-אפ עם Escape וניווט עם חצים
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedImage) return;

      if (e.key === "Escape") {
        closeModal();
      } else if (e.key === "ArrowLeft") {
        if (i18n.language === "he") {
          if (currentImageIndex < galleryImages.length - 1) {
            setSelectedImage(galleryImages[currentImageIndex + 1]);
          }
        } else {
          if (currentImageIndex > 0) {
            setSelectedImage(galleryImages[currentImageIndex - 1]);
          }
        }
      } else if (e.key === "ArrowRight") {
        if (i18n.language === "he") {
          if (currentImageIndex > 0) {
            setSelectedImage(galleryImages[currentImageIndex - 1]);
          }
        } else {
          if (currentImageIndex < galleryImages.length - 1) {
            setSelectedImage(galleryImages[currentImageIndex + 1]);
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, currentImageIndex, galleryImages, i18n.language]);

  // מניעת גלילה בגוף הדף כשהפופ-אפ פתוח
  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedImage]);

  const fetchGalleryImages = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const images = await mediaService.getAll();
      // סינון רק תמונות גלריה (קבצים עם סיומת תמונה וקטגוריית גלריה או ללא קטגוריה)
      const imageFiles = images.filter(
        (file) =>
          file.type.startsWith("image/") &&
          (file.category === "gallery" || !file.category)
      );
      setGalleryImages(imageFiles);
    } catch (error) {
            setError("שגיאה בטעינת התמונות מהשרת");
    } finally {
      setIsLoading(false);
    }
  };

  // Infinite scroll עם IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          displayedCount < galleryImages.length
        ) {
          // טען עוד תמונות כשמגיעים לתחתית
          setDisplayedCount((prev) =>
            Math.min(prev + imagesPerLoad, galleryImages.length)
          );
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px", // התחל טעינה לפני שמגיעים לתחתית
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [displayedCount, galleryImages.length]);

  // תמונות להצגה
  const displayedImages = galleryImages.slice(0, displayedCount);
  const hasMore = displayedCount < galleryImages.length;

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const openModal = (image: MediaFile) => {
    setSelectedImage(image);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const isFirstImage = currentImageIndex === 0;
  const isLastImage = currentImageIndex === galleryImages.length - 1;
  const isRTL = i18n.language === "he";

  // Loading state
  if (isLoading) {
    return (
      <>
        <div className="gallery-detail-container">
          <div className="gallery-detail-header">
            <h1 className="gallery-detail-title">{t("gallery")}</h1>
            <p className="gallery-detail-subtitle">
              תמונות מהסדנאות והמוצרים שלנו
            </p>
          </div>
          {/* <div className="loading-message">{t("loading")}</div> */}
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <div className="gallery-detail-container">
          <div className="gallery-detail-header">
            <h1 className="gallery-detail-title">{t("gallery")}</h1>
            <p className="gallery-detail-subtitle">
              תמונות מהסדנאות והמוצרים שלנו
            </p>
          </div>
          <div className="error-message">
            <p>{error}</p>
            <button
              className="retry-button"
              onClick={fetchGalleryImages}
              style={{
                background: "#4f2e03",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "25px",
                cursor: "pointer",
                marginTop: "1rem",
              }}
            >
              נסה שוב
            </button>
          </div>
        </div>
      </>
    );
  }

  // Empty state
  if (galleryImages.length === 0) {
    return (
      <>
        <div className="gallery-detail-container">
          <div className="gallery-detail-header">
            <h1 className="gallery-detail-title">{t("gallery")}</h1>
            <p className="gallery-detail-subtitle">
              תמונות מהסדנאות והמוצרים שלנו
            </p>
          </div>
          <div className="empty-gallery">
            <p>אין תמונות זמינות כרגע</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="gallery-detail-container">
        <div className="gallery-detail-header">
          <h1 className="gallery-detail-title">{t("gallery")}</h1>
          <p className="gallery-detail-subtitle-bolt">
            {t("galleryDescriptionBolt")}
          </p>
          <p className="gallery-detail-subtitle">{t("galleryDescription")}</p>
        </div>

        <div className="gallery-detail-grid">
          {displayedImages.map((image) => {
            const handleImageKeyDown = (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openModal(image);
              }
            };

            return (
            <div
              className="gallery-detail-item"
              key={image.id}
              onClick={() => openModal(image)}
              onKeyDown={handleImageKeyDown}
              role="button"
              tabIndex={0}
              aria-label={`${t("viewImage") || "צפה בתמונה"}: ${image.name}`}
            >
              <img
                src={image.url}
                alt={image.name}
                className="gallery-detail-image"
                loading="lazy" // Lazy loading מובנה לדפדפן
                onError={(e) => {
                  // אם התמונה לא נטענת, הצג תמונת ברירת מחדל
                  const target = e.target as HTMLImageElement;
                  target.src = "/images/image-error.jpg";
                  target.onerror = null; // מניעת לולאה אינסופית
                }}
              />
            </div>
            );
          })}
        </div>

        {/* IntersectionObserver target - אלמנט בלתי נראה בתחתית */}
        {hasMore && (
          <div
            ref={observerTarget}
            style={{
              height: "20px",
              width: "100%",
              marginTop: "2rem",
            }}
            aria-hidden="true"
          />
        )}

        {/* הודעת סיום */}
        {!hasMore && galleryImages.length > 0 && (
          <div className="page-info" style={{ marginTop: "2rem" }}>
            {galleryImages.length} {t("images")}
          </div>
        )}
      </div>

      {/* Image Modal - מועבר ל-body באמצעות Portal */}
      {selectedImage &&
        createPortal(
          <div
            className="image-modal-overlay"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-label="תצוגה מוגדלת של תמונה"
          >
            <div className="image-modal-content">
              <button
                className="image-modal-close"
                onClick={closeModal}
                aria-label="סגור"
              >
                ×
              </button>

              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="image-modal-image"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/images/image-error.jpg";
                  target.onerror = null;
                }}
              />

              {/* חצים בתחתית במקום קבוע */}
              {galleryImages.length > 1 && (
                <div className="image-modal-nav-container">
                  {/* חץ קודם - תמיד מוצג */}
                  <button
                    className={`image-modal-nav image-modal-prev ${
                      isRTL ? "rtl-nav" : ""
                    }`}
                    onClick={goToPrevious}
                    disabled={isFirstImage}
                    aria-label={isRTL ? "תמונה הבאה" : "תמונה קודמת"}
                    aria-disabled={isFirstImage}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d={isRTL ? "M15 18L9 12L15 6" : "M9 18L15 12L9 6"}
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {/* אינדיקטור תמונה נוכחית */}
                  <div className="image-modal-counter">
                    {currentImageIndex + 1} / {galleryImages.length}
                  </div>

                  {/* חץ הבא - תמיד מוצג */}
                  <button
                    className={`image-modal-nav image-modal-next ${
                      isRTL ? "rtl-nav" : ""
                    }`}
                    onClick={goToNext}
                    disabled={isLastImage}
                    aria-label={isRTL ? "תמונה קודמת" : "תמונה הבאה"}
                    aria-disabled={isLastImage}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d={isRTL ? "M9 18L15 12L9 6" : "M15 18L9 12L15 6"}
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default GalleryDetail;
